import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Alert,
  Spinner,
  Dropdown,
  ProgressBar,
  Tabs,
  Tab
} from 'react-bootstrap';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const PreviousSessionResources = () => {
  const { currentUser, userProfile } = useAuth();
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [selectedResource, setSelectedResource] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [availableCohorts, setAvailableCohorts] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [selectedVisibility, setSelectedVisibility] = useState('private');
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    totalViews: 0,
    totalDownloads: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (currentUser && userProfile) {
      loadSessionResources();
      loadCohorts();
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    filterAndSearchResources();
  }, [resources, searchTerm, filterStatus, filterVisibility]);

  const loadSessionResources = async () => {
    try {
      setLoading(true);

      const resourcesCollection = collection(db, 'sessionResources');
      let resourcesQuery;
      if (['admin', 'qa'].includes(userProfile?.role)) {
        resourcesQuery = query(
          resourcesCollection,
          orderBy('sessionDate', 'desc'),
          limit(200)
        );
      } else {
        resourcesQuery = query(
          resourcesCollection,
          where('facilitatorId', '==', currentUser.uid),
          orderBy('sessionDate', 'desc'),
          limit(100)
        );
      }

      const snapshot = await getDocs(resourcesQuery);
      const resourcesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sessionDate: doc.data().sessionDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate()
      }));

      setResources(resourcesData);
      calculateStats(resourcesData);

    } catch (error) {
      console.error('Error loading session resources:', error);
      showAlert('Failed to load session resources', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      published: data.filter(r => r.status === 'published').length,
      draft: data.filter(r => r.status === 'draft').length,
      totalViews: data.reduce((sum, r) => sum + (r.analytics?.views || 0), 0),
      totalDownloads: data.reduce((sum, r) => sum + (r.analytics?.downloads || 0), 0)
    };
    setStats(stats);
  };

  const loadCohorts = async () => {
    try {
      const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
      const cohortsData = cohortsSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      cohortsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAvailableCohorts(cohortsData);
    } catch (error) {
      console.error('Error loading cohorts:', error);
    }
  };

  const filterAndSearchResources = () => {
    let filtered = [...resources];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.metadata?.topics?.some(topic =>
          topic.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(resource => resource.status === filterStatus);
    }

    // Visibility filter
    if (filterVisibility !== 'all') {
      filtered = filtered.filter(resource => resource.visibility === filterVisibility);
    }

    setFilteredResources(filtered);
    setCurrentPage(1);
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleViewResource = (resource) => {
    setSelectedResource(resource);
    setShowViewModal(true);
  };

  const handlePublishResource = async (resourceId, cohortIds, visibility, publish = true) => {
    try {
      const updatePayload = {
        visibility,
        cohortIds,
        updatedAt: Timestamp.now()
      };
      if (publish) {
        updatePayload.status = 'published';
        updatePayload.publishedAt = Timestamp.now();
      }

      await updateDoc(doc(db, 'sessionResources', resourceId), updatePayload);

      showAlert('Resource published successfully', 'success');
      setShowPublishModal(false);
      loadSessionResources();
    } catch (error) {
      console.error('Error publishing resource:', error);
      showAlert('Failed to publish resource', 'danger');
    }
  };

  const openPublishModal = (resource) => {
    setSelectedResource(resource);
    const currentCohortIds = Array.isArray(resource?.cohortIds)
      ? resource.cohortIds
      : (resource?.cohortId ? [resource.cohortId] : []);
    setSelectedCohortIds(currentCohortIds);
    setSelectedVisibility(resource?.visibility || 'private');
    setShowPublishModal(true);
  };

  const toggleCohortSelection = (cohortId) => {
    setSelectedCohortIds((prev) => {
      if (prev.includes(cohortId)) {
        return prev.filter((id) => id !== cohortId);
      }
      return [...prev, cohortId];
    });
  };

  const handleArchiveResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to archive this resource?')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'sessionResources', resourceId), {
        status: 'archived',
        updatedAt: Timestamp.now()
      });

      showAlert('Resource archived successfully', 'success');
      loadSessionResources();
    } catch (error) {
      console.error('Error archiving resource:', error);
      showAlert('Failed to archive resource', 'danger');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to permanently delete this resource? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'sessionResources', resourceId));
      showAlert('Resource deleted successfully', 'success');
      loadSessionResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      showAlert('Failed to delete resource', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    const badgeMap = {
      draft: 'secondary',
      published: 'success',
      archived: 'warning'
    };
    return <Badge bg={badgeMap[status] || 'secondary'}>{status}</Badge>;
  };

  const getVisibilityBadge = (visibility) => {
    const badgeMap = {
      private: 'danger',
      cohort: 'info',
      public: 'success'
    };
    return <Badge bg={badgeMap[visibility] || 'secondary'}>{visibility}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentResources = filteredResources.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredResources.length / itemsPerPage);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading session resources...</p>
      </Container>
    );
  }

  return (
    <Container fluid>
      {alert.show && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <h3 className="text-primary mb-0">{stats.total}</h3>
              <small className="text-muted">Total Resources</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <h3 className="text-success mb-0">{stats.published}</h3>
              <small className="text-muted">Published</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <h3 className="text-secondary mb-0">{stats.draft}</h3>
              <small className="text-muted">Drafts</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h3 className="text-info mb-0">{stats.totalViews}</h3>
              <small className="text-muted">Total Views</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h3 className="text-warning mb-0">{stats.totalDownloads}</h3>
              <small className="text-muted">Total Downloads</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={5}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by title, description, or topic..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e.target.value)}
              >
                <option value="all">All Visibility</option>
                <option value="private">Private</option>
                <option value="cohort">Cohort</option>
                <option value="public">Public</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <Button variant="outline-primary" onClick={loadSessionResources}>
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Resources Table */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Session Resources</h5>
          <Badge bg="secondary">{filteredResources.length} resources</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {currentResources.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
              <p className="text-muted mt-3">
                {resources.length === 0
                  ? 'No session resources found. Recordings from ReadAI will appear here automatically.'
                  : 'No resources match your current filters.'}
              </p>
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Duration</th>
                  <th>Participants</th>
                  <th>Status</th>
                  <th>Visibility</th>
                  <th>Views</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentResources.map((resource) => (
                  <tr key={resource.id}>
                    <td>
                      <strong>{resource.title}</strong>
                      <br />
                      <small className="text-muted">{resource.description}</small>
                      <br />
                      {resource.metadata?.topics?.slice(0, 3).map((topic, idx) => (
                        <Badge key={idx} bg="light" text="dark" className="me-1">
                          {topic}
                        </Badge>
                      ))}
                    </td>
                    <td>{formatDate(resource.sessionDate)}</td>
                    <td>{formatDuration(resource.duration)}</td>
                    <td>{resource.metadata?.participants?.length || 0}</td>
                    <td>{getStatusBadge(resource.status)}</td>
                    <td>{getVisibilityBadge(resource.visibility)}</td>
                    <td>
                      <div>
                        <i className="bi bi-eye"></i> {resource.analytics?.views || 0}
                      </div>
                      <div>
                        <i className="bi bi-download"></i> {resource.analytics?.downloads || 0}
                      </div>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleViewResource(resource)}>
                            <i className="bi bi-eye"></i> View
                          </Dropdown.Item>
                          {resource.resources?.reportUrl && (
                            <Dropdown.Item
                              href={resource.resources.reportUrl}
                              target="_blank"
                            >
                              <i className="bi bi-box-arrow-up-right"></i> Open in ReadAI
                            </Dropdown.Item>
                          )}
                          {resource.status === 'draft' && (
                            <Dropdown.Item onClick={() => openPublishModal(resource)}>
                              <i className="bi bi-send"></i> Publish
                            </Dropdown.Item>
                          )}
                          {resource.status === 'published' && (
                            <Dropdown.Item onClick={() => openPublishModal(resource)}>
                              <i className="bi bi-people"></i> Update Cohorts
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleArchiveResource(resource.id)}>
                            <i className="bi bi-archive"></i> Archive
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            onClick={() => handleDeleteResource(resource.id)}
                            className="text-danger"
                          >
                            <i className="bi bi-trash"></i> Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredResources.length)} of {filteredResources.length}
              </div>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="mx-2">Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* View Resource Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedResource?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedResource && (
            <Tabs defaultActiveKey="summary" className="mb-3">
              <Tab eventKey="summary" title="Summary">
                <div className="mb-3">
                  <h6>Session Information</h6>
                  <p><strong>Date:</strong> {formatDate(selectedResource.sessionDate)}</p>
                  <p><strong>Duration:</strong> {formatDuration(selectedResource.duration)}</p>
                  <p><strong>Participants:</strong> {selectedResource.metadata?.participants?.length || 0}</p>
                </div>
                {selectedResource.metadata?.topics && (
                  <div className="mb-3">
                    <h6>Topics Discussed</h6>
                    <ul>
                      {selectedResource.metadata.topics.map((topic, idx) => (
                        <li key={idx}>{topic}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedResource.metadata?.actionItems && (
                  <div className="mb-3">
                    <h6>Action Items</h6>
                    <ul>
                      {selectedResource.metadata.actionItems.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Tab>
              <Tab eventKey="resources" title="Resources">
                <div className="d-grid gap-2">
                  {selectedResource.resources?.transcriptUrl && (
                    <Button
                      variant="outline-primary"
                      href={selectedResource.resources.transcriptUrl}
                      target="_blank"
                    >
                      <i className="bi bi-file-text"></i> View Transcript
                    </Button>
                  )}
                  {selectedResource.resources?.summaryUrl && (
                    <Button
                      variant="outline-info"
                      href={selectedResource.resources.summaryUrl}
                      target="_blank"
                    >
                      <i className="bi bi-file-earmark-text"></i> View Summary
                    </Button>
                  )}
                  {selectedResource.resources?.recordingUrl && (
                    <Button
                      variant="outline-success"
                      href={selectedResource.resources.recordingUrl}
                      target="_blank"
                    >
                      <i className="bi bi-play-circle"></i> View Recording
                    </Button>
                  )}
                  {selectedResource.resources?.reportUrl && (
                    <Button
                      variant="outline-warning"
                      href={selectedResource.resources.reportUrl}
                      target="_blank"
                    >
                      <i className="bi bi-box-arrow-up-right"></i> Open ReadAI Report
                    </Button>
                  )}
                </div>
              </Tab>
              <Tab eventKey="participants" title="Participants">
                {selectedResource.metadata?.participants && (
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedResource.metadata.participants.map((participant, idx) => (
                        <tr key={idx}>
                          <td>{participant.name}</td>
                          <td>{participant.email}</td>
                          <td>
                            <Badge bg="info">{participant.role || 'Participant'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Publish Resource Modal */}
      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Publish Resource</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Publishing will make this resource available according to the visibility settings.</p>
          <Form.Group className="mb-3">
            <Form.Label>Visibility</Form.Label>
            <Form.Select
              value={selectedVisibility}
              onChange={(e) => setSelectedVisibility(e.target.value)}
            >
              <option value="private">Private</option>
              <option value="cohort">Cohort</option>
              <option value="public">Public</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Cohort visibility will only be available to selected cohorts.
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>Additional Cohorts</Form.Label>
            <div style={{ maxHeight: 220, overflowY: 'auto' }} className="border rounded p-2">
              {availableCohorts.length === 0 && (
                <div className="text-muted small">No cohorts available.</div>
              )}
              {availableCohorts.map((cohort) => (
                <Form.Check
                  key={cohort.id}
                  type="checkbox"
                  id={`cohort-${cohort.id}`}
                  label={cohort.name || cohort.id}
                  checked={selectedCohortIds.includes(cohort.id)}
                  onChange={() => toggleCohortSelection(cohort.id)}
                />
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPublishModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              handlePublishResource(
                selectedResource.id,
                selectedCohortIds,
                selectedVisibility,
                selectedResource?.status === 'draft'
              )
            }
          >
            {selectedResource?.status === 'draft' ? 'Publish' : 'Update'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PreviousSessionResources;
